import { Component, ElementRef, HostListener, Input, OnInit, ViewEncapsulation, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { WebchatBoxComponent, WebchatButtonComponent, DefaultPosition } from '@galaxy/conversation/webchat-widget';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { AuthService } from '../../services/auth.service';
import { initCookieProxy } from '../../util/cookie';
import { WebchatConfigService } from '../../services/webchat-config.service';
import { LocationRow, WebchatLocationService } from '../../services/webchat-location.service';

const BUBBLE_SIZE = 100;
const CLOSED_HEIGHT_DESKTOP = 180;
const CLOSED_HEIGHT_MOBILE = 120;
const MAX_WIDTH = 432;
const MAX_HEIGHT = 700;

@Component({
  selector: 'app-webchat-multi-location-root',
  imports: [
    GalaxyAvatarModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatInputModule,
    TranslateModule,
    WebchatBoxComponent,
    WebchatButtonComponent,
  ],
  templateUrl: './webchat-multi-location-root.component.html',
  styleUrls: ['./webchat-multi-location-root.component.scss'],
  providers: [WebchatLocationService],
  encapsulation: ViewEncapsulation.Emulated,
})
export class WebchatMultiLocationRootComponent implements OnInit {
  @Input() widgetId = '';
  @Input() embedded = false;

  private readonly configSvc = inject(WebchatConfigService);
  private readonly locationService = inject(WebchatLocationService);
  private readonly authService = inject(AuthService);
  private readonly el = inject(ElementRef);
  private parentOriginValue = '*';

  protected readonly isOpen = signal(false);
  protected readonly view = signal<'picker' | 'chat'>('picker');
  protected readonly selectedLocation = signal<LocationRow | null>(null);
  protected readonly currentPage = signal(1);
  protected readonly isMobile = signal(false);

  protected readonly config = this.configSvc.multiLocationConfig;
  protected readonly position = signal<'right' | 'left'>(DefaultPosition);
  protected readonly configError = signal(false);

  protected readonly locations = toSignal(this.locationService.locations$, { initialValue: [] as LocationRow[] });
  protected readonly isLoading = toSignal(this.locationService.isLoading$, { initialValue: true });
  protected readonly hasMore = toSignal(this.locationService.hasMore$, { initialValue: false });
  protected readonly hasPrev = toSignal(this.locationService.hasPrev$, { initialValue: false });
  protected readonly error = toSignal(this.locationService.error$, { initialValue: false });

  // TODO: wire conversation auth (WARP-2402)
  protected readonly currentParticipantId = signal<string | null>(null);

  constructor() {
    const el = this.el.nativeElement;
    this.parentOriginValue = el.getAttribute('data-parent-origin') || window.location.origin;
    this.isMobile.set(el.getAttribute('data-is-mobile') === 'true');
    initCookieProxy(el.getAttribute('data-cookies') || '', this.parentOriginValue);

    const urlEncoded = el.getAttribute('parent-url');
    if (urlEncoded) this.configSvc.hostPageUrl.set(decodeURIComponent(urlEncoded));
    const hostname = el.getAttribute('parent-hostname');
    if (hostname) this.configSvc.hostPageHostname.set(hostname);

    effect(() => {
      if (!this.config() || this.embedded) return;
      const open = this.isOpen();
      const mobile = this.isMobile();
      const closedHeight = mobile ? CLOSED_HEIGHT_MOBILE : CLOSED_HEIGHT_DESKTOP;
      window.parent.postMessage(
        {
          type: 'webchat:resize',
          detail: {
            widgetId: this.widgetId,
            width: open ? MAX_WIDTH : BUBBLE_SIZE,
            height: open ? MAX_HEIGHT : closedHeight,
            position: this.position(),
            widgetTopSpace: 0,
          },
        },
        this.parentOriginValue,
      );
    });

  }

  @HostListener('window:message', ['$event'])
  onMessage(event: MessageEvent): void {
    if (event.source !== window.parent) return;
    if (event.data?.type === 'webchat:mobile-change') {
      this.isMobile.set(!!event.data.detail?.isMobile);
      return;
    }
    if (event.data?.type === 'webchat:url-change') {
      const url = event.data.detail?.url;
      if (typeof url === 'string' && url.length > 0) {
        this.configSvc.hostPageUrl.set(url);
      }
    }
  }

  ngOnInit(): void {
    this.setupFocusNotification();
    this.setupLinkInterceptor();
    this.configSvc.loadMultiLocationWidget(this.widgetId).then(() => {
      const cfg = this.config();
      if (cfg) {
        this.position.set(cfg.position ?? DefaultPosition);
        this.locationService.init(this.widgetId);
        window.parent.postMessage({ type: 'webchat:ready', detail: { widgetId: this.widgetId } }, this.parentOriginValue);
      }
    }).catch(() => {
      this.configError.set(true);
    });
  }

  protected openWidget(): void {
    const loc = this.selectedLocation();
    if (loc) {
      this.authService.loadSession(loc.namespace);
      this.view.set('chat');
    } else {
      this.view.set('picker');
    }
    this.isOpen.set(true);
  }

  protected closeWidget(): void {
    this.isOpen.set(false);
    this.currentPage.set(1);
    this.locationService.setSearchTerm('');
  }

  protected retrySearch(): void {
    this.currentPage.set(1);
    this.locationService.setSearchTerm('');
  }

  protected onSearch(event: Event): void {
    this.currentPage.set(1);
    this.locationService.setSearchTerm((event.target as HTMLInputElement).value);
  }

  protected selectLocation(loc: LocationRow): void {
    this.authService.loadSession(loc.namespace);
    this.selectedLocation.set(loc);
    this.view.set('chat');
  }

  protected goBack(): void {
    this.authService.clearToken(this.selectedLocation()?.namespace);
    this.view.set('picker');
    this.selectedLocation.set(null);
  }

  protected nextPage(): void {
    this.locationService.nextPage();
    this.currentPage.update((p) => p + 1);
  }

  protected prevPage(): void {
    this.locationService.prevPage();
    this.currentPage.update((p) => p - 1);
  }

  private setupFocusNotification(): void {
    if (this.embedded) return;

    const notify = (focused: boolean) => {
      window.parent.postMessage({ type: 'webchat:input-focus', detail: { focused } }, this.parentOriginValue);
    };

    const isTextInput = (el: EventTarget | null): boolean =>
      el instanceof HTMLTextAreaElement ||
      el instanceof HTMLInputElement ||
      (el instanceof HTMLElement && el.isContentEditable);

    this.el.nativeElement.addEventListener('focusin', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(true);
    });
    this.el.nativeElement.addEventListener('focusout', (e: FocusEvent) => {
      if (isTextInput(e.target)) notify(false);
    });
  }

  private setupLinkInterceptor(): void {
    this.el.nativeElement.addEventListener('click', (event: MouseEvent) => {
      const anchor = (event.target as HTMLElement).closest('a');
      if (!anchor?.href) return;

      event.preventDefault();
      if (anchor.target === '_blank') {
        window.open(anchor.href, '_blank', 'noopener,noreferrer');
      } else {
        window.parent.location.href = anchor.href;
      }
    });
  }
}
